import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Textarea — multi-line text control.
 *
 * Focus treatment is deliberately identical to `Input` and `Select`:
 * `focus:border-primary` + a flush `focus:ring-1 focus:ring-primary`. It previously used
 * `focus-visible:ring-2` together with `ring-offset-2`, which paints a 2px offset gap and a 2px
 * ring OUTSIDE the existing 1px border — three stacked edges that read as a double/thick focus
 * border. `ring-1` with no offset sits flush on the border, giving one consistent 1px accent.
 * It also referenced `border-input` / `ring-ring`, neither of which exist in this token set
 * (see src/styles/globals.css @theme), so the ring colour fell back arbitrarily.
 */
const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, ...props }, ref) => {
  return (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-[var(--radius-input)] border border-outlines bg-white px-3 py-2 text-sm text-text-primary placeholder:text-text-placeholder focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Textarea.displayName = "Textarea"

export { Textarea }
